import { getBrandLang } from '@prismamedia/one-brandkey';
import { BrandKey } from '../__generated__/queries-web';
import { useGetBrandPrompts } from '../apollo/queries/prompts.web.graphql';
import {
  BRAND_KEY_LANGUAGE,
  GENERATE_PROMPT,
  PARAPHRASE_PROMPT,
  prependJsonFormatInstructions,
} from '../components/TextSuggestion/constants';

interface Prompt {
  label: string;
  body: string;
  field?: string;
  retry?: boolean;
}

interface PromptMenus {
  generateMenu: Prompt[];
  retryMenu: Prompt[];
}

export const useOpenAiPrompts = ({
  brandKey,
  body = '',
  lead = '',
  title = '',
  field,
  publishedAt,
}: {
  brandKey: BrandKey;
  body?: string;
  lead?: string;
  title?: string;
  fieldValue?: string;
  field: string;
  publishedAt?: string;
}): PromptMenus & { maxTokens?: number; temperature?: number } => {
  const brandLang = BRAND_KEY_LANGUAGE[getBrandLang(brandKey)];
  const { data } = useGetBrandPrompts(brandKey);

  const generatePrompt = GENERATE_PROMPT(
    brandLang,
    title,
    field,
    body,
    publishedAt,
  );
  const paraphrasePrompt = PARAPHRASE_PROMPT(brandLang, title);

  const customPrompts = data?.prompts?.length
    ? data.prompts
        .map((prompt) => {
          const promptField =
            prompt.field === 'Clickbait'
              ? 'partnerTitle'
              : prompt.field?.toLowerCase();
          const hasFieldVariable =
            promptField && prompt.body.includes(`\${${promptField}}`);
          const hasAnyVariable = prompt.body.includes('${');

          return {
            label: prompt.label,
            body: prependJsonFormatInstructions(
              replacePromptVariables(prompt.body, {
                body,
                lead,
                field: promptField,
              }),
            ),
            field: promptField,
            retry: hasFieldVariable || !hasAnyVariable,
          };
        })
        .filter((prompt) => !prompt.field || prompt.field === field)
    : [];

  const generateMenu = [generatePrompt, ...customPrompts];

  const retryMenu = [paraphrasePrompt, ...customPrompts];

  // Field-specific AI parameters
  const getFieldParameters = () => {
    if (field === 'discoverTitle') {
      return {
        maxTokens: 16384,
        temperature: 1,
      };
    }
    return {};
  };

  return {
    generateMenu,
    retryMenu,
    ...getFieldParameters(),
  };
};

/**
 * Replaces variables in prompt body with actual values
 */
const replacePromptVariables = (
  promptBody: string,
  variables: { body: string; lead: string; field?: string },
): string => {
  let processedBody = promptBody
    .replace(/\$\{body\}/g, variables.body)
    .replace(/\$\{lead\}/g, variables.lead);

  if (variables.field) {
    processedBody = processedBody.replace(
      new RegExp(`\\$\\{${variables.field}\\}`, 'g'),
      '${currentField}',
    );
  }

  return processedBody;
};
